// 주문 알림 켜기/끄기 토글.
// 마이페이지에 둔다. 권한/구독 상태를 읽어 버튼 라벨과 동작을 결정한다.
//  - 웹/PWA: 웹 푸시(pushClient). 네이티브 앱(Capacitor): FCM(nativePush) - WebView 는 웹 푸시 미지원.
//  - 둘 다 아닌(미지원 브라우저/미설정) 환경은 안내만 노출하고 토글을 숨긴다.
//  - iOS 브라우저는 홈 화면에 추가한 PWA 에서만 푸시가 동작한다.

import { useEffect, useState } from "react";
import { isNativeApp } from "@/shared/platform.ts";
import {
  getNotificationPermission,
  isCurrentlySubscribed,
  isPushSupported,
  subscribeUserPush,
  unsubscribeUserPush,
} from "./pushClient.ts";
import { disableNativePush, enableNativePush, isNativePushEnabled } from "./nativePush.ts";

// 사용자 푸시 알림 구독/해제 토글.
export default function PushToggle() {
  // 네이티브 앱은 FCM 경로라 웹 푸시 지원 여부와 무관하게 항상 지원이다.
  const [supported] = useState(() => isNativeApp || isPushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) {
      return;
    }
    if (isNativeApp) {
      setSubscribed(isNativePushEnabled());
      return;
    }
    void isCurrentlySubscribed().then(setSubscribed);
  }, [supported]);

  if (!supported) {
    return (
      <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
        이 브라우저는 주문 알림을 지원하지 않습니다. 홈 화면에 앱을 추가하면 알림을 받을 수 있어요.
      </p>
    );
  }

  async function onEnable() {
    setBusy(true);
    setMessage(null);
    const result = isNativeApp ? await enableNativePush() : await subscribeUserPush();
    setBusy(false);
    if (result.ok) {
      setSubscribed(true);
      setMessage("주문 알림이 켜졌습니다.");
      return;
    }
    // 실패 사유별 안내 (손님 카피 톤). 권한 안내는 실행 환경(앱/브라우저)에 맞춘다.
    const deniedMsg = isNativeApp
      ? "알림 권한이 꺼져 있어요. 휴대폰 설정 > 애플리케이션 > 열린축산에서 알림을 허용해 주세요."
      : "알림 권한이 꺼져 있어요. 브라우저 설정에서 허용해 주세요.";
    const reasonMsg: Record<string, string> = {
      denied: deniedMsg,
      unsupported: "이 브라우저는 알림을 지원하지 않습니다.",
      "not-configured": "알림 기능이 아직 준비 중입니다.",
      error: "알림을 켜지 못했습니다. 잠시 후 다시 시도해 주세요.",
    };
    setMessage(reasonMsg[result.reason] ?? "알림을 켜지 못했습니다.");
  }

  async function onDisable() {
    setBusy(true);
    setMessage(null);
    try {
      if (isNativeApp) {
        await disableNativePush();
      } else {
        await unsubscribeUserPush();
      }
      setSubscribed(false);
      setMessage("주문 알림을 껐습니다.");
    } catch {
      setMessage("알림을 끄지 못했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  }

  // 웹 전용 권한 표시(네이티브는 Notification API 가 없어 "unsupported" 로 나오므로 차단 안내에 쓰지 않는다).
  const permission = isNativeApp ? "default" : getNotificationPermission();

  return (
    <div className="flex flex-col gap-2 rounded-md border border-ink-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-base font-medium">주문 알림</p>
          <p className="text-xs text-ink-600">
            주문 접수/준비/완료 상태를 푸시로 알려드립니다.
          </p>
        </div>
        <button
          type="button"
          onClick={subscribed ? onDisable : onEnable}
          disabled={busy || permission === "denied"}
          className={`min-h-touch shrink-0 rounded-md px-4 text-sm font-semibold disabled:opacity-50 ${
            subscribed
              ? "border border-ink-300 text-ink-700 hover:bg-ink-100"
              : "bg-brand-600 text-white hover:bg-brand-700"
          }`}
        >
          {busy ? "처리 중..." : subscribed ? "끄기" : "켜기"}
        </button>
      </div>
      {permission === "denied" ? (
        <p className="text-xs text-warning-700">
          알림 권한이 차단되어 있어요. 브라우저 사이트 설정에서 알림을 허용해 주세요.
        </p>
      ) : null}
      {message ? <p className="text-xs text-ink-600">{message}</p> : null}
    </div>
  );
}
